import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@xinosolutions/xs-auth-react";
import AppHeader from "./AppHeader";
import AppSidebar from "./AppSidebar";
import PortalBranding from "./PortalBranding";
import { styled } from "@mui/material/styles";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { update_user_region_api } from "../../DAL/Login/Login";
import { ClassRoomProvider } from "../../Hooks/ClassRoom";
import { ScreenHighlightProvider } from "../../Hooks/ScreenHighlight";
import ScreenHighlightLayer from "../../components/ClassRoom/ScreenHighlightLayer";
import { SIDEBAR_WIDTH } from "../../utils/layoutUtils";
import ComponentPopup from "../../components/GeneralComponents/ComponentPopup";
import GoForTest from "../../components/Examinations/GoForTest";
import cookieStorage from "../../utils/cookieStorage";
import AppLoader from "../../components/GeneralComponents/AppLoader";
import DashboardSessionError from "../../components/auth/DashboardSessionError";
import { useLearnixProduct } from "../../contexts/LearnixProductContext";
import Welcome from "../../pages/Welcome/Welcome";
import { normalizeStudentApiFields } from "../../utils/studentDisplay";

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 65;

const RootStyle = styled("div")({
  display: "flex",
  minHeight: "100%",
  overflow: "visible",
});

const MainStyle = styled("div")(({ theme, pathname }) => ({
  flexGrow: 1,
  minWidth: 0,
  // overflow:auto on this flex child grows with the page and never becomes the
  // scroller, which prevents position:sticky (Feeds side columns, dashboard).
  overflow:
    pathname.includes("dashboard") || pathname.includes("feeds")
      ? "visible"
      : "auto",
  minHeight: "100%",
  paddingTop: APP_BAR_MOBILE,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up("lg")]: {
    paddingTop: APP_BAR_DESKTOP,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

function workspaceApplyKey(sessionEpoch, workspace) {
  const profile = workspace?.associate;
  if (!profile) return `${sessionEpoch}:`;
  const id = profile._id || profile.id || "";
  const userType = profile.user_type || "";
  return `${sessionEpoch}:${id}:${userType}`;
}

export default function DashboardLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionRetrying, setSessionRetrying] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(SIDEBAR_WIDTH);
  const { sessionEpoch } = useAuth();
  const appliedKeyRef = useRef("");
  const prevProfileRef = useRef({ id: "", userType: "" });
  const prevNeedsOnboardingRef = useRef(false);

  const {
    setUserInfo,
    setUnreadMessageCount,
    setStartedMeeting,
    setInitSettings,
    setStudentsGroup,
    setConversation,
    initSettings,
    START_SOCKET_FORCEFULLY,
    STOP_SOCKET_FORCEFULLY,
  } = useContentSetting();
  const {
    ready,
    loading: learnixLoading,
    workspace,
    needsOnboarding,
    failureReason,
    refreshLearnixSession,
  } = useLearnixProduct();
  const showWelcome = needsOnboarding;
  const effectiveDrawerWidth = showWelcome ? 0 : drawerWidth;

  const applyKey = useMemo(
    () => workspaceApplyKey(sessionEpoch, workspace),
    [sessionEpoch, workspace],
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePermanentDrawer = () => {
    if (drawerWidth) {
      setDrawerWidth(0);
    } else {
      setDrawerWidth(SIDEBAR_WIDTH);
    }
  };

  const handle_update_language = useCallback(async () => {
    const response = await fetch("https://ipinfo.io/json?token=702699ce736041");
    const region_data = await response.json();
    const { language, country } = region_data;

    if (!language) {
      const locale = new Intl.Locale("und", { region: country });
      region_data.language = locale.maximize().language;
    }

    region_data.country_lang = `${region_data.language}-${country}`;
    setUserInfo((old) => ({ ...old, user_region: region_data }));
    const postData = { user_region: region_data };
    await update_user_region_api(postData);
  }, [setUserInfo]);

  const applyWorkspace = useCallback(
    (result) => {
      // Portal roles hydrate as `associate`. Never accept `admin_user` here —
      // admins must use xs-learnix-admin-app (see accessDenied below).
      const profile = normalizeStudentApiFields(result?.associate);
      if (!profile) {
        setIsLoading(false);
        return;
      }

      const nextId = profile._id || profile.id || "";
      const nextType = profile.user_type || "";
      const prev = prevProfileRef.current;
      const identityChanged =
        Boolean(prev.id) && (prev.id !== nextId || prev.userType !== nextType);
      prevProfileRef.current = { id: nextId, userType: nextType };

      if (needsOnboarding || result?.needs_onboarding) {
        setUserInfo(profile);
        setIsLoading(false);
        return;
      }

      setUserInfo(profile);
      if (nextId) {
        // START_SOCKET_FORCEFULLY disconnects any previous member socket first.
        START_SOCKET_FORCEFULLY(nextId);
      }
      if (!profile?.user_region) {
        void handle_update_language();
      }
      setUnreadMessageCount(result.unread_message_count);
      setStudentsGroup(
        (result.student_group || []).map((s) => normalizeStudentApiFields(s)),
      );
      setConversation(result.conversation);

      const today = new Date().toISOString().split("T")[0];
      const skippedDate = cookieStorage.getItem("testSkipped");
      if (result.examination && skippedDate !== today) {
        setInitSettings({ ...result, isExam: true });
      } else {
        setInitSettings(result);
      }

      setStartedMeeting(result.started_call);
      setIsLoading(false);

      // Role / account switch: leave associate-only routes so teacher nav matches the page.
      if (identityChanged && pathname !== "/dashboard") {
        navigate("/dashboard", { replace: true });
      }
    },
    [
      handle_update_language,
      navigate,
      needsOnboarding,
      pathname,
      setConversation,
      setInitSettings,
      setStartedMeeting,
      setStudentsGroup,
      setUnreadMessageCount,
      setUserInfo,
      START_SOCKET_FORCEFULLY,
    ],
  );

  useEffect(() => {
    appliedKeyRef.current = "";
    setIsLoading(true);
  }, [sessionEpoch]);

  useEffect(() => {
    if (prevNeedsOnboardingRef.current && !needsOnboarding) {
      appliedKeyRef.current = "";
      setIsLoading(true);
    }
    prevNeedsOnboardingRef.current = needsOnboarding;
  }, [needsOnboarding]);

  // Admin XS identities get `admin_user` from shared member_init — block them here
  // the same way admin-app blocks non-admins missing `admin_user`.
  const adminMustUseAdminPortal =
    ready && Boolean(workspace?.admin_user) && !workspace?.associate;
  const accessDenied = Boolean(failureReason) || adminMustUseAdminPortal;
  const sessionErrorReason =
    failureReason ||
    (adminMustUseAdminPortal
      ? "This account must sign in through the admin portal."
      : null);

  useEffect(() => {
    if (learnixLoading) {
      return;
    }
    if (accessDenied) {
      setIsLoading(false);
      return;
    }
    if (!ready || !workspace) {
      return;
    }
    // Re-apply when session epoch OR workspace member identity / role changes
    // (XS Auth multi-account switch may refresh workspace without a new epoch).
    if (appliedKeyRef.current === applyKey) {
      return;
    }
    appliedKeyRef.current = applyKey;
    applyWorkspace(workspace);
  }, [
    accessDenied,
    applyKey,
    applyWorkspace,
    learnixLoading,
    ready,
    workspace,
  ]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Clear presence only when leaving the authenticated dashboard shell.
  // Use a ref so ContentSetting re-renders (unstable callback identity historically)
  // never tear down a socket that just started connecting.
  const stopSocketRef = useRef(STOP_SOCKET_FORCEFULLY);
  stopSocketRef.current = STOP_SOCKET_FORCEFULLY;
  useEffect(() => {
    return () => {
      void stopSocketRef.current();
    };
  }, []);

  if (isLoading || learnixLoading || (!ready && !accessDenied)) {
    return (
      <AppLoader wrapperClassName="circular-progress circular-progress--viewport" />
    );
  }

  if (accessDenied) {
    return (
      <RootStyle>
        <AppHeader
          drawerWidth={0}
          handleDrawerToggle={handleDrawerToggle}
          handlePermanentDrawer={handlePermanentDrawer}
          hideNavigation
        />
        <MainStyle pathname={pathname}>
          <DashboardSessionError
            reason={sessionErrorReason}
            retrying={sessionRetrying}
            onRetry={async () => {
              setSessionRetrying(true);
              try {
                await refreshLearnixSession();
              } finally {
                setSessionRetrying(false);
              }
            }}
          />
        </MainStyle>
        <footer
          className="xino-watermark xino-watermark--shell"
          role="contentinfo"
          aria-label="Powered by Xino Solutions"
        >
          <span className="xino-watermark__line" lang="en" dir="ltr">Powered by</span>
          <span className="xino-watermark__brand" lang="en" dir="ltr">Xino Solutions</span>
        </footer>
      </RootStyle>
    );
  }

  return (
    <>
      <ScreenHighlightProvider>
        <ClassRoomProvider>
          <RootStyle>
            <PortalBranding />
            <AppHeader
              drawerWidth={effectiveDrawerWidth}
              handleDrawerToggle={handleDrawerToggle}
              handlePermanentDrawer={handlePermanentDrawer}
              hideNavigation={showWelcome}
            />
            {!showWelcome ? (
              <AppSidebar
                drawerWidth={drawerWidth}
                handleDrawerToggle={handleDrawerToggle}
                mobileOpen={mobileOpen}
              />
            ) : null}
            <MainStyle pathname={pathname}>
              {showWelcome ? <Welcome /> : <Outlet />}
            </MainStyle>
            <footer
              className="xino-watermark xino-watermark--shell"
              role="contentinfo"
              aria-label="Powered by Xino Solutions"
            >
              <span className="xino-watermark__line" lang="en" dir="ltr">Powered by</span>
              <span className="xino-watermark__brand" lang="en" dir="ltr">Xino Solutions</span>
            </footer>
          </RootStyle>
          <ScreenHighlightLayer />
        </ClassRoomProvider>
      </ScreenHighlightProvider>
      <ComponentPopup
        openPopup={Boolean(initSettings.isExam)}
        setOpenPopup={() => {}}
        componentToPassDown={
          <GoForTest {...{ setInitSettings, initSettings }} />
        }
        hideCross={true}
      />
    </>
  );
}
